import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Radar,
  Check,
  X,
  ExternalLink,
  Loader2,
  LogOut,
  Inbox,
  Users,
} from "lucide-react";
import {
  adminLogout,
  approveMention,
  fetchAdminAuthStatus,
  fetchReviewMentions,
  rejectMention,
} from "@/lib/api";
import { ADMIN_REVIEW } from "@/constants/testIds/admin";

const SENTIMENT_STYLES = {
  positive: "border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981]",
  neutral: "border-[#64748b]/40 bg-[#64748b]/10 text-[#cbd5e1]",
  cautionary: "border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#f59e0b]",
};

function ConfidenceBadge({ value }) {
  if (value === null || value === undefined) {
    return (
      <span className="border border-[#334155] bg-[#111827] px-1.5 py-0.5 font-mono text-[10px] text-[#94a3b8]">
        n/a
      </span>
    );
  }
  const pct = Math.round(value * 100);
  let cls = "border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981]";
  if (value < 0.6) cls = "border-[#f43f5e]/40 bg-[#f43f5e]/10 text-[#f43f5e]";
  else if (value < 0.75) cls = "border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#f59e0b]";
  return (
    <span className={`border ${cls} px-1.5 py-0.5 font-mono text-[10px]`}>
      {pct}%
    </span>
  );
}

function VoiceMatchPanel({ mention }) {
  const vm = mention.voice_match;
  if (!vm) return null;

  const scoresEntries = Object.entries(vm.scores || {}).sort(
    ([, a], [, b]) => b - a,
  );
  const winner = vm.winner;
  const gap = typeof vm.margin_gap === "number" ? vm.margin_gap : 0;
  const requiredGap = typeof vm.required_gap === "number" ? vm.required_gap : 0.03;
  const threshold = typeof vm.threshold === "number" ? vm.threshold : 0.75;
  const contextUsed = vm.context_used || "unknown";
  const rejectReason = vm.reject_reason;

  // Color coding: green if gap is comfortably above required (≥2× the
  // guard threshold), amber if it passes but by <2×, red if it failed.
  let gapStyle = "border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981]";
  let gapLabel = `gap ${gap.toFixed(3)} — comfortable`;
  if (rejectReason) {
    gapStyle = "border-[#f43f5e]/40 bg-[#f43f5e]/10 text-[#f43f5e]";
    gapLabel = `gap ${gap.toFixed(3)} — REJECTED`;
  } else if (gap < requiredGap * 2) {
    gapStyle = "border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#f59e0b]";
    gapLabel = `gap ${gap.toFixed(3)} — thin (need ≥${requiredGap.toFixed(2)})`;
  }

  const contextStyle =
    contextUsed === "solo"
      ? "border-[#3b82f6]/40 bg-[#3b82f6]/10 text-[#3b82f6]"
      : contextUsed === "interview"
      ? "border-[#a855f7]/40 bg-[#a855f7]/10 text-[#a855f7]"
      : "border-[#334155] bg-[#0f172a] text-[#94a3b8]";

  return (
    <div
      data-testid={ADMIN_REVIEW.voicePanel(mention.id)}
      className="mt-3 border border-[#1e293b] bg-[#0b1220] p-2.5"
    >
      <div className="mb-1.5 flex items-center gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#64748b]">
          voice fingerprint
        </span>
        <span
          data-testid={ADMIN_REVIEW.voiceContextChip(mention.id)}
          className={`border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider ${contextStyle}`}
        >
          {contextUsed}
        </span>
        <span
          data-testid={ADMIN_REVIEW.voiceGapChip(mention.id)}
          className={`border px-1.5 py-0.5 font-mono text-[9px] ${gapStyle}`}
        >
          {gapLabel}
        </span>
      </div>
      <div
        data-testid={ADMIN_REVIEW.voiceScoresList(mention.id)}
        className="flex flex-wrap items-center gap-1.5"
      >
        {scoresEntries.map(([speaker, score]) => {
          const isWinner = speaker === winner;
          const cls = isWinner
            ? "border-[#10b981]/50 bg-[#10b981]/15 text-[#10b981]"
            : "border-[#334155] bg-[#0f172a] text-[#94a3b8]";
          return (
            <span
              key={speaker}
              className={`border px-1.5 py-0.5 font-mono text-[10px] ${cls}`}
            >
              {isWinner ? "✓ " : ""}
              speaker {speaker}: {score.toFixed(3)}
            </span>
          );
        })}
        <span className="font-mono text-[9px] text-[#64748b]">
          threshold {threshold.toFixed(2)}
        </span>
      </div>
      {rejectReason && (
        <div className="mt-1.5 font-mono text-[10px] text-[#f43f5e]">
          {rejectReason}
        </div>
      )}
    </div>
  );
}

function ReviewRow({ mention, onApprove, onReject, isBusy }) {
  const dateLabel = mention.date ? new Date(mention.date).toLocaleDateString() : "—";
  return (
    <tr
      data-testid={ADMIN_REVIEW.row(mention.id)}
      className="border-b border-[#1e293b] align-top transition-colors hover:bg-[#0f172a]/60"
    >
      <td className="whitespace-nowrap px-3 py-3 align-top font-mono text-[11px] text-[#94a3b8]">
        {dateLabel}
      </td>
      <td className="px-3 py-3 align-top">
        <div className="text-sm text-[#f8fafc]">{mention.expert_name || mention.expert_id}</div>
        <div className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
          {mention.source || "unknown"}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-3 align-top">
        <div className="font-mono text-sm font-semibold text-[#3b82f6]">
          {mention.ticker || "—"}
        </div>
        <div className="mt-0.5 text-[11px] text-[#94a3b8]">{mention.company || ""}</div>
      </td>
      <td className="px-3 py-3 align-top text-center">
        <ConfidenceBadge value={mention.extraction_confidence} />
      </td>
      <td className="px-3 py-3 align-top">
        <div className="text-sm text-[#f8fafc]">{mention.summary || "—"}</div>
        {mention.quote && (
          <div className="mt-2 border-l-2 border-[#334155] pl-2 text-[12px] italic text-[#94a3b8]">
            "{mention.quote}"
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {mention.sentiment && (
            <span
              className={`border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                SENTIMENT_STYLES[mention.sentiment] || SENTIMENT_STYLES.neutral
              }`}
            >
              {mention.sentiment}
            </span>
          )}
          {mention.source_url && (
            <a
              href={mention.source_url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 border border-[#1e293b] bg-[#0f172a] px-2 py-0.5 font-mono text-[10px] text-[#94a3b8] transition-colors hover:border-[#3b82f6] hover:text-[#3b82f6]"
            >
              source <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <VoiceMatchPanel mention={mention} />
      </td>
      <td className="whitespace-nowrap px-3 py-3 align-top">
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={isBusy}
            data-testid={ADMIN_REVIEW.approveButton(mention.id)}
            onClick={() => onApprove(mention)}
            className="inline-flex items-center gap-1 border border-[#10b981]/60 bg-[#10b981]/10 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#10b981] transition-colors hover:bg-[#10b981]/20 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" /> Approve
          </button>
          <button
            type="button"
            disabled={isBusy}
            data-testid={ADMIN_REVIEW.rejectButton(mention.id)}
            onClick={() => onReject(mention)}
            className="inline-flex items-center gap-1 border border-[#f43f5e]/60 bg-[#f43f5e]/10 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#f43f5e] transition-colors hover:bg-[#f43f5e]/20 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" /> Reject
          </button>
        </div>
      </td>
    </tr>
  );
}

function RejectDialog({ open, mention, onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setReason("");
  }, [open, mention]);
  if (!open || !mention) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div
        data-testid={ADMIN_REVIEW.rejectDialog}
        className="w-full max-w-md border border-[#1e293b] bg-[#111827] p-5"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#f43f5e]">
          Reject mention
        </div>
        <div className="mt-2 text-sm text-[#f8fafc]">
          <span className="font-mono text-[#3b82f6]">{mention.ticker}</span> — {mention.expert_name}
        </div>
        <div className="mt-1 text-xs text-[#94a3b8]">{mention.summary}</div>
        <label className="mt-4 block text-[11px] font-mono uppercase tracking-wider text-[#64748b]">
          Reason (optional)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. wrong ticker attribution, off-topic quote…"
          data-testid={ADMIN_REVIEW.rejectReasonInput}
          className="mt-1 w-full border border-[#1e293b] bg-[#0f172a] px-3 py-2 text-sm text-[#f8fafc] placeholder:text-[#64748b] focus:border-[#f43f5e] focus:outline-none focus:ring-1 focus:ring-[#f43f5e]"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid={ADMIN_REVIEW.rejectCancelButton}
            className="border border-[#1e293b] bg-[#0f172a] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={busy}
            data-testid={ADMIN_REVIEW.rejectConfirmButton}
            className="inline-flex items-center gap-1 border border-[#f43f5e] bg-[#f43f5e] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#e11d48] disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
            Confirm reject
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminReview() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | authed | anon
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchReviewMentions();
      setMentions(data?.mentions || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load review queue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let alive = true;
    fetchAdminAuthStatus()
      .then((r) => {
        if (!alive) return;
        if (r?.authenticated) {
          setStatus("authed");
          loadQueue();
        } else {
          setStatus("anon");
          navigate("/admin/login", { replace: true });
        }
      })
      .catch(() => {
        if (alive) {
          setStatus("anon");
          navigate("/admin/login", { replace: true });
        }
      });
    return () => {
      alive = false;
    };
  }, [loadQueue, navigate]);

  const onApprove = async (m) => {
    setBusyId(m.id);
    try {
      await approveMention(m.id);
      setMentions((prev) => prev.filter((x) => x.id !== m.id));
      toast.success(`Approved ${m.ticker} — ${m.expert_name}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const onReject = (m) => setRejectTarget(m);

  const onRejectConfirm = async (reason) => {
    if (!rejectTarget) return;
    const m = rejectTarget;
    setBusyId(m.id);
    try {
      await rejectMention(m.id, reason || undefined);
      setMentions((prev) => prev.filter((x) => x.id !== m.id));
      toast.success(`Rejected ${m.ticker} — ${m.expert_name}`);
      setRejectTarget(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  const onLogout = async () => {
    try {
      await adminLogout();
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  if (status !== "authed") {
    return (
      <div
        data-testid="admin-review-checking"
        className="fixed inset-0 z-50 bg-[#0b1120]"
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      data-testid={ADMIN_REVIEW.page}
      className="min-h-screen bg-[#0b1120] text-[#f8fafc]"
    >
      <header className="sticky top-0 z-30 border-b border-[#1e293b] bg-[#0b1120]/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-3 md:gap-6 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center border border-[#f59e0b]/40 bg-[#f59e0b]/10">
              <Radar className="h-4 w-4 text-[#f59e0b]" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight text-[#f8fafc]">
                SIGNAL EDGE
              </div>
              <div className="hidden font-mono text-[9px] uppercase tracking-[0.2em] text-[#f59e0b] sm:block">
                admin · review queue
              </div>
            </div>
          </Link>
          <div className="flex-1" />
          <Link
            to="/admin/waitlist"
            data-testid={ADMIN_REVIEW.waitlistNavLink}
            className="inline-flex items-center gap-1.5 border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc]"
          >
            <Users className="h-3.5 w-3.5" /> Waitlist
          </Link>
          <span
            data-testid={ADMIN_REVIEW.countBadge}
            className="border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[#94a3b8]"
          >
            {mentions.length} pending
          </span>
          <button
            type="button"
            onClick={onLogout}
            data-testid={ADMIN_REVIEW.logoutButton}
            className="inline-flex items-center gap-1.5 border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc]"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-6 md:px-6">
        <div className="mb-4 flex flex-col gap-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
            M5 · Review Queue
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-[#f8fafc]">
            Approve or reject pending mentions
          </h1>
          <p className="text-sm text-[#94a3b8]">
            Rows are sorted lowest-confidence first. Approve pushes a mention onto the public feed;
            reject soft-deletes it and keeps an audit trail.
          </p>
        </div>

        {loading ? (
          <div
            data-testid={ADMIN_REVIEW.loading}
            className="flex items-center justify-center border border-[#1e293b] bg-[#111827] py-16 text-sm text-[#94a3b8]"
          >
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading review queue…
          </div>
        ) : mentions.length === 0 ? (
          <div
            data-testid={ADMIN_REVIEW.emptyState}
            className="flex flex-col items-center justify-center border border-dashed border-[#1e293b] bg-[#0f172a] py-20 text-center"
          >
            <Inbox className="mb-3 h-8 w-8 text-[#334155]" />
            <div className="text-sm font-semibold text-[#f8fafc]">Review queue is empty</div>
            <div className="mt-1 text-xs text-[#94a3b8]">
              New low-confidence extractions and manual backfills will land here for triage.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#1e293b] bg-[#111827]">
            <table
              data-testid={ADMIN_REVIEW.table}
              className="w-full min-w-[880px] text-left text-sm"
            >
              <thead className="border-b border-[#1e293b] bg-[#0f172a] font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
                <tr>
                  <th className="px-3 py-2 font-normal">Date</th>
                  <th className="px-3 py-2 font-normal">Expert / Source</th>
                  <th className="px-3 py-2 font-normal">Ticker</th>
                  <th className="px-3 py-2 text-center font-normal">Confidence</th>
                  <th className="px-3 py-2 font-normal">Summary &amp; Quote</th>
                  <th className="px-3 py-2 font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mentions.map((m) => (
                  <ReviewRow
                    key={m.id}
                    mention={m}
                    onApprove={onApprove}
                    onReject={onReject}
                    isBusy={busyId === m.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 border-t border-[#1e293b] pt-4 text-[11px] leading-relaxed text-[#64748b]">
          Signal Edge · Admin actions are logged with server timestamps. Rejects retain a soft-delete
          row (<span className="font-mono">status=dropped</span>) with an optional reason for audit.
        </div>
      </main>

      <RejectDialog
        open={!!rejectTarget}
        mention={rejectTarget}
        busy={!!busyId && rejectTarget && busyId === rejectTarget.id}
        onCancel={() => setRejectTarget(null)}
        onConfirm={onRejectConfirm}
      />
    </div>
  );
}
